import React, { useState, useEffect } from 'react';
import { Package, CheckCircle, AlertTriangle, AlertCircle, Clock } from 'lucide-react';
import { serviceTypeFromJob } from '../../../../services/maintenance/serviceType';
import { getServiceTypeName } from '../../../../services/maintenance/serviceType';
import { getLinkedEquipment } from '../../../../utils/maintenanceEquipmentLinker';
import { getJobFrequencyDays } from '../../../../utils/intervalHelpers';
import { FEATURE_USE_RA_FFE_MAINTENANCE } from '../../../../config/featureFlags';
import { useCrew } from '../../../../hooks/useCrew';
// FT-189: Centralized crew name resolution
import { resolveCrewName } from '../crew/utils/crewHelpers';

/**
 * Equipment Status Table
 * Shows individual equipment items with their service status for a specific job
 * Now includes checkbox selection for completing maintenance
 * 
 * IMPORTANT: Equipment passed to this component is already filtered by MaintenanceCheckView
 * using resolveJobEquipment(). This component does NOT re-filter equipment.
 */
const EquipmentStatusTable = ({ job, equipment = [], selectedItems = [], onSelectionChange, maintenanceHistory = [] }) => {
  const [localSelectedItems, setLocalSelectedItems] = useState(selectedItems);
  const [selectAll, setSelectAll] = useState(false);
  const { crew } = useCrew();
  
  // FT-189: Use centralized crew name resolution helper
  const getCrewDisplayName = (crewId) => {
    if (!crewId) return null;
    return resolveCrewName(crewId, crew || []);
  };

  // IMPORTANT: Equipment passed here is already resolved by MaintenanceCheckView
  // We do NOT filter it again - this ensures Navigation Equipment Check 
  // only shows RA-approved equipment items
  const linkedEquipment = Array.isArray(equipment) ? equipment : [];
  
  // Debug logging for Navigation Equipment Check
  if (job?.category === 'Navigation' && job?.id === 'nav.equipment.check') {

  }

  // Update local state when props change or when linked equipment changes
  useEffect(() => {
    if (selectedItems.length > 0) {
      setLocalSelectedItems(selectedItems);
    } else if (linkedEquipment.length > 0) {
      // Default to all equipment selected
      const allIds = linkedEquipment.map(eq => eq.id);
      setLocalSelectedItems(allIds);
      setSelectAll(true);
      if (onSelectionChange) {
        onSelectionChange(allIds);
      }
    }
  }, [selectedItems, linkedEquipment.length]);

  // Handle checkbox changes
  const handleItemToggle = (itemId) => {
    const newSelection = localSelectedItems.includes(itemId)
      ? localSelectedItems.filter(id => id !== itemId)
      : [...localSelectedItems, itemId];

    setLocalSelectedItems(newSelection);
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }

    // Update select all state
    if (linkedEquipment) {
      setSelectAll(newSelection.length === linkedEquipment.length);
    }
  };

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    if (!linkedEquipment) return;

    const newSelection = selectAll ? [] : linkedEquipment.map(eq => eq.id);
    setLocalSelectedItems(newSelection);
    setSelectAll(!selectAll);

    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };

  // No equipment found
  if (linkedEquipment.length === 0) {
    return null;
  }

  // Get the service type for this job
  const serviceType = serviceTypeFromJob(job);
  const serviceTypeName = getServiceTypeName(serviceType);

  // Calculate status for each equipment item
  const equipmentWithStatus = linkedEquipment.map(eq => {
    // Check for service data from multiple sources
    let lastService = null;
    
    // 1. First try to find from maintenance history for this specific job and equipment
    if (maintenanceHistory && maintenanceHistory.length > 0) {
      const relevantHistory = maintenanceHistory
        .filter(h => 
          h.jobId === job.id && 
          h.equipmentIds && 
          h.equipmentIds.includes(eq.id)
        )
        .sort((a, b) => new Date(b.completedAt || b.date) - new Date(a.completedAt || a.date));
      
      if (relevantHistory.length > 0) {
        const latest = relevantHistory[0];
        // FT-31 PHASE 3b: Use record.date (user-entered completion date) NOT completedAt (save timestamp)
        lastService = {
          date: latest.date || latest.completedAt,
          inspector: getCrewDisplayName(latest.performedBy || latest.inspector) || 'Unknown'
        };
      }
    }
    
    // 2. Fall back to computed equipment service status (calculated from job completions)
    // BUG-005 FIX: Use computed _lastServiceDate field instead of deprecated lastServiceAt
    if (!lastService && eq._lastServiceDate) {
      lastService = {
        date: eq._lastServiceDate,
        inspector: getCrewDisplayName(eq._lastServiceBy) || 'Unknown'
      };
    }

    // NOTE: Removed serviceHistory fallback - that field no longer exists (BUG-005 fix)
    // Service history is now tracked in job.completions[], not equipment.serviceHistory[]

    // Calculate status based on frequency and last service
    let status = 'never';
    let daysUntilDue = null;
    // BUG-005 FIX: Use computed _nextServiceDue from job completions
    let nextDue = eq._nextServiceDue;

    if (lastService) {
      const lastServiceDate = new Date(lastService.date);
      const today = new Date();
      const daysSinceService = Math.floor((today - lastServiceDate) / (1000 * 60 * 60 * 24));
      
      // Calculate next due if not already set
      if (!nextDue && job) {
        const frequencyDays = getJobFrequencyDays(job);
        daysUntilDue = frequencyDays - daysSinceService;
      } else if (nextDue) {
        // Calculate days until the specified nextDue date
        const nextDueDate = new Date(nextDue);
        daysUntilDue = Math.floor((nextDueDate - today) / (1000 * 60 * 60 * 24));
      }

      if (daysUntilDue !== null) {
        if (daysUntilDue < 0) {
          status = 'overdue';
        } else if (daysUntilDue <= 30) {
          status = 'due-soon';
        } else {
          status = 'compliant';
        }
      }
    }

    return {
      ...eq,
      lastService,
      status,
      daysUntilDue,
      nextDue
    };
  });

  // Get status icon and color
  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'due-soon':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'overdue':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'never':
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
      default:
        return null;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800';
      case 'due-soon':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'never':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusText = (status, daysUntilDue) => {
    switch (status) {
      case 'compliant':
        return `Due in ${daysUntilDue} days`;
      case 'due-soon':
        return `Due in ${daysUntilDue} days`;
      case 'overdue':
        return `${Math.abs(daysUntilDue)} days overdue`;
      case 'never':
        return 'Never serviced';
      default:
        return '';
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      <div className="bg-slate-700 px-5 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-white">
              Equipment Service Status
            </h3>
            <p className="mt-1 text-sm text-slate-300">
              Track maintenance status for {linkedEquipment.length} equipment {linkedEquipment.length === 1 ? 'item' : 'items'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2 text-sm text-white">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAllToggle}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <span>Select All</span>
            </label>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Select
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Equipment
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last {serviceTypeName}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {equipmentWithStatus.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={localSelectedItems.includes(item.id)}
                    onChange={() => handleItemToggle(item.id)}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Package className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item.name || item.type || 'Unknown Equipment'}
                      </div>
                      {item.serialNumber && (
                        <div className="text-sm text-gray-500">
                          S/N: {item.serialNumber}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{item.location || '-'}</div>
                  {item.specifications && (
                    <div className="text-sm text-gray-500">{item.specifications}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {item.lastService ? 
                      new Date(item.lastService.date).toLocaleDateString() : 
                      'Never'
                    }
                  </div>
                  {item.lastService?.inspector && (
                    <div className="text-sm text-gray-500">
                      by {item.lastService.inspector}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {getStatusIcon(item.status)}
                    <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(item.status)}`}>
                      {getStatusText(item.status, item.daysUntilDue)}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EquipmentStatusTable;